import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import {
    ChartConfig,
    ChartContainer,
    ChartLegend,
    ChartLegendContent,
    ChartTooltip,
    ChartTooltipContent,
} from "@/components/ui/chart";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { endOfMonth, format, parse, startOfMonth } from "date-fns";
import { CalendarIcon, CheckIcon, ChevronsUpDown, Save, TrendingUp } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, LabelList, Legend, Pie, PieChart, XAxis, YAxis } from "recharts";
import { trpc } from "~/api/trpc";
import Content from "~/components/layout/Content";
import { PractitionerNameWithCategory } from "~/components/practitioners/PractitionerNameWithCategory";
import { Skeleton } from "~/components/ui/skeleton";
import { AppointmentStatus } from "~/types/server-types/AppointmentStatus";
import * as XLSX from 'xlsx';
import { Switch } from "~/components/ui/switch";
import LocalDateTime from "~/lib/localdatetime";

const renderCustomizedLabel = (value: number) => {
    return `${value} €`;
};

type RevenueGraphVisualProps = {
    startDate: LocalDateTime;
    endDate: LocalDateTime;
    storeIds: number[];
    practitionerIds: number[];
    treatmentVariantIds: string[];
    timesOfDay: ('morning' | 'afternoon' | 'evening')[];
}

type RevenueGraphViewMode = 'day' | 'month';

const RevenueGraphVisual = ({ startDate, endDate, storeIds, practitionerIds, treatmentVariantIds, timesOfDay }: RevenueGraphVisualProps) => {
    const [enabled, setEnabled] = useState<boolean>(false);

    const [viewMode, setViewMode] = useState<RevenueGraphViewMode>('day');

    const chartConfig = {
        desktop: {
            label: "Desktop",
            color: "var(--color-primary-400)",
        },
    } satisfies ChartConfig

    const revenueOverTimeQuery = trpc.analytics.getRevenueOverTime.useQuery({
        startDate: startDate.toJson(),
        endDate: endDate.toJson(),
        storeIds: storeIds,
        practitionerIds: practitionerIds,
        treatmentVariantIds: treatmentVariantIds,
        timesOfDay: timesOfDay,
    }, {
        staleTime: 60 * 1000,
        enabled: enabled,
    });

    const totalRevenue = useMemo(() => {
        if (!enabled) return 0;
        return revenueOverTimeQuery.data?.totalRevenue ?? 0;
    }, [revenueOverTimeQuery.data]); // Dependency only on data

    const revenueOverTime = useMemo(() => {
        if (!enabled) return [];
        if (!revenueOverTimeQuery.data) {
            return [];
        }

        const revenueByDate = revenueOverTimeQuery.data.revenueByDate;

        if (viewMode === 'day') {
            // Daily view (same as original)
            const dailyData = Object.entries(revenueByDate).map(([dateStr, revenue]) => ({
                day: dateStr,
                revenue: revenue,
            }));
            return dailyData.sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime());
        } else {
            // Monthly view - group data by month
            const monthlyRevenue: Record<string, number> = {};

            Object.entries(revenueByDate).forEach(([dateStr, revenue]) => {
                const date = new Date(dateStr);
                const monthKey = format(date, 'MMM yyyy'); // Format as "Jan 2025", "Feb 2025", etc.

                if (!monthlyRevenue[monthKey]) {
                    monthlyRevenue[monthKey] = 0;
                }
                monthlyRevenue[monthKey] += revenue;
            });

            // Convert to array and sort by date
            const monthlyData = Object.entries(monthlyRevenue).map(([month, revenue]) => ({
                day: month, // Using "day" as the key for consistency with chart
                revenue: revenue,
            }));

            // Sort by parsing the month strings
            return monthlyData.sort((a, b) => {
                const dateA = parse(a.day, 'MMM yyyy', new Date());
                const dateB = parse(b.day, 'MMM yyyy', new Date());
                return dateA.getTime() - dateB.getTime();
            });
        }
    }, [revenueOverTimeQuery.data, viewMode]); // Added viewMode as dependency

    if (!enabled) {
        return (
            <Card className='max-h-[500px]'>
                <CardHeader>
                    <CardTitle>
                        Revenue Monthly & Daily
                    </CardTitle>
                    <CardDescription>
                        Enable analytics to view revenue data
                    </CardDescription>
                    <div className="flex items-center space-x-2 mt-4">
                        <Switch
                            checked={enabled}
                            onCheckedChange={setEnabled}
                            id="analytics-enabled"
                        />
                        <label htmlFor="analytics-enabled" className="text-sm font-medium">
                            Enable
                        </label>
                    </div>
                </CardHeader>
            </Card>
        );
    }

    if (revenueOverTimeQuery.isLoading) {
        return (
            <Card className='max-h-[500px]'>
                <CardHeader>
                    <CardTitle>
                        <Skeleton className="h-7 w-1/2 rounded-md" /> {/* For "Total: ..." text */}
                    </CardTitle>
                    <CardDescription>
                        <Skeleton className="h-4 w-3/4 rounded-md" /> {/* For "Revenue from ..." text */}
                    </CardDescription>
                </CardHeader>
                <CardContent className='h-full'>
                    <Skeleton className='w-full h-full max-h-[300px] rounded-md' /> {/* For ChartContainer */}
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className='max-h-[500px]'>
            <CardHeader>
                <div className="flex justify-between items-center">
                    <div>
                        <CardTitle>
                            <span className='text-2xl font-normal'>Total: {totalRevenue.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</span>
                        </CardTitle>
                        <CardDescription>
                            Revenue from {format(new Date(startDate.getDisplayDate()), "dd.MM.yyyy")} to {format(new Date(endDate.getDisplayDate()), "dd.MM.yyyy")}
                        </CardDescription>
                    </div>
                    <div className="flex items-center space-x-1">
                        <Button
                            variant={viewMode === 'day' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setViewMode('day')}
                        >
                            Daily
                        </Button>
                        <Button
                            variant={viewMode === 'month' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setViewMode('month')}
                        >
                            Monthly
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className='h-full w-full'>
                <ChartContainer config={chartConfig} className='w-full h-full max-h-[300px]'>
                    <AreaChart
                        data={revenueOverTime}
                        {...{
                            overflow: 'visible'
                        }}
                    >
                        <CartesianGrid vertical={false} />
                        <XAxis
                            dataKey="day"
                            tickLine={false}
                            axisLine={false}
                            tickMargin={8}
                            tickFormatter={viewMode === 'month' ? undefined : (date) => format(new Date(date), 'dd.MM')}
                        />
                        <YAxis
                            tickLine={false}
                            axisLine={false}
                            tickMargin={3}
                            tickCount={5}
                            tickFormatter={(value) => value.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                        />
                        <ChartTooltip
                            cursor={false}
                            contentStyle={{
                                position: 'absolute'
                            }}
                            content={
                                <ChartTooltipContent
                                    indicator="line"
                                    formatter={(value) => {
                                        return value.toLocaleString("de-DE", { style: "currency", currency: "EUR" })
                                    }}
                                />
                            }
                        />
                        <Area
                            dataKey="revenue"
                            type="linear"
                            fill="var(--color-desktop)"
                            fillOpacity={0.4}
                            stroke="var(--color-desktop)"
                            dot={{
                                fill: "var(--color-primary-400)"
                            }}
                            activeDot={{
                                r: 6,
                            }}
                        >
                            {viewMode === 'month' && (
                                <LabelList
                                    position="top"
                                    offset={5}
                                    className="fill-foreground"
                                    fontSize={12}
                                    formatter={renderCustomizedLabel}
                                    dataKey='revenue'
                                />
                            )}
                        </Area>
                    </AreaChart>
                </ChartContainer>
            </CardContent>
        </Card>
    )
}


type FreeAndBusyTimeslotsVisualProps = {
    startDate: LocalDateTime;
    endDate: LocalDateTime;
    storeIds: number[];
    practitionerIds: number[];
    treatmentVariantIds: string[];
    timesOfDay: ('morning' | 'afternoon' | 'evening')[];
    timeslotLengthMinutes: number;
};

const practitionerColors = [
    '#fca5a5', // red-300
    '#fdba74', // orange-300
    '#fcd34d', // amber-300
    '#bef264', // lime-300
    '#86efac', // green-300
    '#5eead4', // teal-300
    '#67e8f9', // cyan-300
    '#7dd3fc', // sky-300
    '#a5b4fc', // indigo-300
    '#d8b4fe', // purple-300
    '#f0abfc', // fuchsia-300
    '#f9a8d4', // pink-300
];

type FreeAndBusyTimeslotsViewMode = 'free' | 'busy';

const FreeAndBusyTimeslotsVisual = ({ startDate, endDate, storeIds, practitionerIds, treatmentVariantIds, timesOfDay, timeslotLengthMinutes }: FreeAndBusyTimeslotsVisualProps) => {
    const [enabled, setEnabled] = useState<boolean>(false);

    const [viewMode, setViewMode] = useState<FreeAndBusyTimeslotsViewMode>('free');
    const freeAndBusyTimeslotsQuery = trpc.analytics.getDailyFreeAndBusyTimeslots.useQuery({
        startDate: startDate.toJson(),
        endDate: endDate.toJson(),
        storeIds: storeIds,
        practitionerIds: practitionerIds,
        treatmentVariantIds: treatmentVariantIds,
        timesOfDay: timesOfDay,
        timeslotLengthMinutes: timeslotLengthMinutes,
    }, {
        staleTime: 60 * 1000, // 1 minute
        enabled: enabled,
    });
    const totalFreeTimeslots = useMemo(() => {
        if (!freeAndBusyTimeslotsQuery.data?.dailyFreeAndBusyTimeslots) {
            return 0;
        }
        return freeAndBusyTimeslotsQuery.data.dailyFreeAndBusyTimeslots.reduce((total, dailyData) =>
            total + dailyData.practitioners.reduce((subtotal, prac) => subtotal + prac.free_timeslots, 0), 0);
    }, [freeAndBusyTimeslotsQuery.data]);
    const totalBusyTimeslots = useMemo(() => {
        if (!freeAndBusyTimeslotsQuery.data?.dailyFreeAndBusyTimeslots) {
            return 0;
        }
        return freeAndBusyTimeslotsQuery.data.dailyFreeAndBusyTimeslots.reduce((total, dailyData) =>
            total + dailyData.practitioners.reduce((subtotal, prac) => subtotal + prac.busy_timeslots, 0), 0);
    }, [freeAndBusyTimeslotsQuery.data]);
    const freePercentage = useMemo(() => {
        if (totalFreeTimeslots + totalBusyTimeslots === 0) {
            return 0;
        }
        return Math.round(10000 * (totalFreeTimeslots / (totalFreeTimeslots + totalBusyTimeslots))) / 100;
    }, [totalFreeTimeslots, totalBusyTimeslots])
    const busyPercentage = useMemo(() => {
        if (totalFreeTimeslots + totalBusyTimeslots === 0) {
            return 0;
        }
        return Math.round(10000 * (totalBusyTimeslots / (totalFreeTimeslots + totalBusyTimeslots))) / 100;
    }, [totalFreeTimeslots, totalBusyTimeslots])
    const practitionersArray = useMemo(() => {
        if (!freeAndBusyTimeslotsQuery.data?.dailyFreeAndBusyTimeslots) {
            return [];
        }
        const practitionersSet = new Set<string>();
        freeAndBusyTimeslotsQuery.data.dailyFreeAndBusyTimeslots.forEach((dailyData) => {
            dailyData.practitioners.forEach((practitioner) => {
                practitionersSet.add(practitioner.name);
            });
        });
        // Sort practitioner names alphabetically for consistent color assignment
        return Array.from(practitionersSet).sort().map((name, idx) => ({
            name,
            color: practitionerColors[idx % practitionerColors.length],
        }));
    }, [freeAndBusyTimeslotsQuery.data]);
    const freeTimeslotsOverTime = useMemo(() => {
        if (!freeAndBusyTimeslotsQuery.data?.dailyFreeAndBusyTimeslots || practitionersArray.length === 0) {
            return [];
        }
        const timeslotsArray = freeAndBusyTimeslotsQuery.data.dailyFreeAndBusyTimeslots;
        const dailyDataMap = new Map<string, Record<string, any>>();
        const practitionerNames = practitionersArray.map(p => p.name);
        timeslotsArray.forEach((dataPoint) => {
            const dayStr = dataPoint.date;
            let dayEntry = dailyDataMap.get(dayStr);
            if (!dayEntry) {
                dayEntry = { day: dayStr };
                // Initialize with practitioner data
                for (const name of practitionerNames) {
                    dayEntry[name] = 0;
                    // Initialize percentage fields for each practitioner
                    dayEntry[`${name}_percent`] = 0;
                }
                dailyDataMap.set(dayStr, dayEntry);
            }

            // First pass: count total timeslots for the day to calculate percentages
            let dayTotalTimeslots = 0;
            dataPoint.practitioners.forEach((practitioner) => {
                if (practitionerNames.includes(practitioner.name)) {
                    dayTotalTimeslots += practitioner.free_timeslots + practitioner.busy_timeslots;
                }
            });

            // Second pass: set values and calculate percentages
            dataPoint.practitioners.forEach((practitioner) => {
                if (practitionerNames.includes(practitioner.name)) {
                    dayEntry[practitioner.name] = practitioner.free_timeslots;
                    // Calculate percentage for this practitioner on this day
                    const totalForPractitioner = practitioner.free_timeslots + practitioner.busy_timeslots;
                    const percentage = totalForPractitioner === 0 ? 0 :
                        Math.round(10000 * (practitioner.free_timeslots / totalForPractitioner)) / 100;
                    dayEntry[`${practitioner.name}_percent`] = percentage;
                }
            });
        });
        const result = Array.from(dailyDataMap.values());
        return result.sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime());
    }, [freeAndBusyTimeslotsQuery.data, practitionersArray]);
    const busyTimeslotsOverTime = useMemo(() => {
        if (!freeAndBusyTimeslotsQuery.data?.dailyFreeAndBusyTimeslots || practitionersArray.length === 0) {
            return [];
        }
        const timeslotsArray = freeAndBusyTimeslotsQuery.data.dailyFreeAndBusyTimeslots;
        const dailyDataMap = new Map<string, Record<string, any>>();
        const practitionerNames = practitionersArray.map(p => p.name);
        timeslotsArray.forEach((dataPoint) => {
            const dayStr = dataPoint.date;
            let dayEntry = dailyDataMap.get(dayStr);
            if (!dayEntry) {
                dayEntry = { day: dayStr };
                // Initialize with practitioner data
                for (const name of practitionerNames) {
                    dayEntry[name] = 0;
                    // Initialize percentage fields for each practitioner
                    dayEntry[`${name}_percent`] = 0;
                }
                dailyDataMap.set(dayStr, dayEntry);
            }

            // First pass: count total timeslots for the day to calculate percentages
            let dayTotalTimeslots = 0;
            dataPoint.practitioners.forEach((practitioner) => {
                if (practitionerNames.includes(practitioner.name)) {
                    dayTotalTimeslots += practitioner.free_timeslots + practitioner.busy_timeslots;
                }
            });

            // Second pass: set values and calculate percentages
            dataPoint.practitioners.forEach((practitioner) => {
                if (practitionerNames.includes(practitioner.name)) {
                    dayEntry[practitioner.name] = practitioner.busy_timeslots;
                    // Calculate percentage for this practitioner on this day
                    const totalForPractitioner = practitioner.free_timeslots + practitioner.busy_timeslots;
                    const percentage = totalForPractitioner === 0 ? 0 :
                        Math.round(10000 * (practitioner.busy_timeslots / totalForPractitioner)) / 100;
                    dayEntry[`${practitioner.name}_percent`] = percentage;
                }
            });
        });
        const result = Array.from(dailyDataMap.values());
        return result.sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime());
    }, [freeAndBusyTimeslotsQuery.data, practitionersArray]);
    // NEW: Create a custom tooltip component that shows percentages
    const CustomTooltipContent = ({ active, payload, label }: any) => {
        if (!active || !payload || !payload.length) {
            return null;
        }

        const dateLabel = format(new Date(label), 'EEEE, dd.MM.yyyy');
        const sortedPayload = [...payload].sort((a, b) => b.value - a.value);

        return (
            <div className="rounded-lg border bg-background p-2 shadow-md">
                <p className="text-xs font-medium">{dateLabel}</p>
                <div className="mt-1.5 space-y-1">
                    {sortedPayload.map((entry, index) => {
                        // Extract the practitioner name and find the percentage
                        const practitionerName = entry.dataKey;
                        const percentKey = `${practitionerName}_percent`;
                        // Find percentage from payload data
                        const percentage = payload[0].payload[percentKey];

                        return entry.value > 0 ? (
                            <div key={`tooltip-${index}`} className="flex items-center justify-between gap-2 text-xs">
                                <div className="flex items-center gap-1.5">
                                    <span
                                        className="h-2.5 w-2.5 rounded-full"
                                        style={{ backgroundColor: entry.color }}
                                    />
                                    <span>{entry.name}</span>
                                </div>
                                <div className="font-medium tabular-nums">
                                    {entry.value} <span className="text-muted-foreground">({percentage.toFixed(1)}%)</span>
                                </div>
                            </div>
                        ) : null;
                    })}
                </div>
            </div>
        );
    };

    const chartConfig = useMemo(() => {
        const config: ChartConfig = {};
        practitionersArray.forEach(practitioner => {
            config[practitioner.name] = {
                label: practitioner.name,
                color: practitioner.color,
            };
        });
        return config;
    }, [practitionersArray]);

    // Calculate total free timeslots per practitioner
    const practitionerTotals = useMemo(() => {
        if (!freeAndBusyTimeslotsQuery.data?.dailyFreeAndBusyTimeslots || practitionersArray.length === 0) {
            return [];
        }
        const totalsFreeMap = new Map<string, number>();
        const totalsBusyMap = new Map<string, number>();
        practitionersArray.forEach(p => totalsFreeMap.set(p.name, 0)); // Initialize map with all practitioners
        practitionersArray.forEach(p => totalsBusyMap.set(p.name, 0)); // Initialize map with all practitioners
        freeAndBusyTimeslotsQuery.data.dailyFreeAndBusyTimeslots.forEach(dailyData => {
            dailyData.practitioners.forEach(prac => {
                if (totalsFreeMap.has(prac.name)) { // Sum only for practitioners in practitionersArray
                    totalsFreeMap.set(prac.name, (totalsFreeMap.get(prac.name) as number) + prac.free_timeslots);
                }
                if (totalsBusyMap.has(prac.name)) { // Sum only for practitioners in practitionersArray
                    totalsBusyMap.set(prac.name, (totalsBusyMap.get(prac.name) as number) + prac.busy_timeslots);
                }
            });
        });
        return practitionersArray
            .map(p => ({
                name: p.name,
                totalFree: totalsFreeMap.get(p.name) || 0,
                totalBusy: totalsBusyMap.get(p.name) || 0,
                freePercentage: (totalsFreeMap.get(p.name) || 0) + (totalsBusyMap.get(p.name) || 0) === 0 ? 0 : Math.round(10000 * ((totalsFreeMap.get(p.name) || 0) / ((totalsFreeMap.get(p.name) || 0) + (totalsBusyMap.get(p.name) || 0)))) / 100,
                busyPercentage: (totalsFreeMap.get(p.name) || 0) + (totalsBusyMap.get(p.name) || 0) === 0 ? 0 : Math.round(10000 * ((totalsBusyMap.get(p.name) || 0) / ((totalsFreeMap.get(p.name) || 0) + (totalsBusyMap.get(p.name) || 0)))) / 100,
                color: p.color,
            }))
            .sort((a, b) => viewMode === 'free' ? b.totalFree - a.totalFree : b.totalBusy - a.totalBusy); // Optional: sort by total descending
    }, [freeAndBusyTimeslotsQuery.data, practitionersArray, viewMode]);

    // Custom legend content to show percentages
    const CustomLegendContent = ({ payload }: any) => {
        if (!payload) return null;

        return (
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 pt-2">
                {payload?.map((entry: any, index: number) => {
                    // Find the corresponding practitioner data to get the percentage
                    const practitionerData = practitionerTotals.find(p => p.name === entry.value);
                    const percentage = viewMode === 'free'
                        ? practitionerData?.freePercentage || 0
                        : practitionerData?.busyPercentage || 0;

                    return (
                        <div key={`legend-item-${index}`} className="flex items-center space-x-1.5">
                            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
                            <span className="text-xs">
                                {entry.value} <span className="text-xs text-muted-foreground">({percentage.toFixed(1)}%)</span>
                            </span>
                        </div>
                    );
                })}
            </div>
        );
    };

    if (!enabled) {
        return (
            <Card className='max-h-[500px]'>
                <CardHeader>
                    <CardTitle>
                        Free and Busy timeslots ({timeslotLengthMinutes} min)
                    </CardTitle>
                    <CardDescription>
                        Enable analytics to view free/busy timeslot data
                    </CardDescription>
                    <div className="flex items-center space-x-2 mt-4">
                        <Switch
                            checked={enabled}
                            onCheckedChange={setEnabled}
                            id="analytics-enabled"
                        />
                        <label htmlFor="analytics-enabled" className="text-sm font-medium">
                            Enable
                        </label>
                    </div>
                </CardHeader>
            </Card>
        );
    }

    if (freeAndBusyTimeslotsQuery.isLoading) {
        return (
            <Card className='max-h-[500px] w-full'>
                <CardHeader>
                    <CardTitle><Skeleton className="h-7 w-1/2 rounded-md" /></CardTitle>
                    <CardDescription><Skeleton className="h-4 w-3/4 rounded-md" /></CardDescription>
                </CardHeader>
                <CardContent className='h-full'>
                    <Skeleton className='w-full h-full max-h-[300px] min-h-[200px] rounded-md' />
                </CardContent>
            </Card>
        );
    }
    return (
        <Card className='w-full'> {/* Increased max-h slightly to accommodate totals */}
            <CardHeader>
                <div className="flex justify-between items-center">
                    <div>
                        <CardTitle>
                            <span className='text-2xl font-normal'>Total {viewMode === 'free' ? 'free' : 'busy'} {timeslotLengthMinutes}-min timeslots: {viewMode === 'free' ? totalFreeTimeslots : totalBusyTimeslots} ({viewMode === 'free' ? freePercentage.toFixed(1) : busyPercentage.toFixed(1)}%)</span>
                        </CardTitle>
                        <CardDescription>
                            {viewMode === 'free' ? 'Free' : 'Busy'} timeslots from {format(new Date(startDate.getDisplayDate()), "dd.MM.yyyy")} to {format(new Date(endDate.getDisplayDate()), "dd.MM.yyyy")}
                        </CardDescription>
                    </div>
                    <div className="flex items-center space-x-1">
                        <Button
                            variant={viewMode === 'free' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setViewMode('free')}
                        >
                            Free
                        </Button>
                        <Button
                            variant={viewMode === 'busy' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setViewMode('busy')}
                        >
                            Busy
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className='h-full w-full flex flex-col'>
                <div className='h-[300px] w-full'> {/* Fixed height container for the chart */}
                    <ChartContainer config={chartConfig} className='w-full h-full'>
                        <AreaChart
                            data={viewMode === 'free' ? freeTimeslotsOverTime : busyTimeslotsOverTime}
                            margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                            accessibilityLayer
                        >
                            <CartesianGrid vertical={false} strokeDasharray="3 3" />
                            <XAxis
                                dataKey="day"
                                tickLine={false}
                                axisLine={false}
                                tickMargin={8}
                                tickFormatter={(value) => format(new Date(value), 'dd.MM')}
                            />
                            <YAxis
                                tickLine={false}
                                axisLine={false}
                                tickMargin={3}
                                tickCount={5}
                                allowDecimals={false}
                            />
                            <ChartTooltip
                                cursor={true}
                                content={<CustomTooltipContent />}
                            />
                            <Legend
                                content={<CustomLegendContent />}
                            />
                            {practitionersArray.map((practitioner) => (
                                <Area
                                    key={practitioner.name}
                                    dataKey={practitioner.name}
                                    type="monotone" // Smoother curves
                                    name={practitioner.name}
                                    fill={practitioner.color}
                                    fillOpacity={0.5} // Slightly reduced opacity for lighter colors
                                    stroke={practitioner.color}
                                    strokeWidth={2}
                                    stackId="a"
                                    dot={{ r: 2, fill: practitioner.color, fillOpacity: 1 }}
                                    activeDot={{ r: 5, strokeWidth: 1, fillOpacity: 1 }}
                                />
                            ))}
                        </AreaChart>
                    </ChartContainer>
                </div>
                {practitionerTotals.length > 0 && (
                    <div className="mt-6 pt-4 border-t">
                        <h3 className="text-sm font-medium mb-3 text-foreground">
                            Aggregated {viewMode === 'free' ? 'Free' : 'Busy'} Timeslots:
                        </h3>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                            {practitionerTotals.map((pracTotal) => (
                                <div key={pracTotal.name} className="flex items-center space-x-2">
                                    <span
                                        className="h-3 w-3 shrink-0 rounded-full"
                                        style={{ backgroundColor: pracTotal.color }}
                                        title={pracTotal.name}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium text-foreground truncate" title={pracTotal.name}>
                                            {pracTotal.name}
                                        </p>
                                    </div>
                                    <div className="text-xs">
                                        <span className="font-semibold">{viewMode === 'free' ? pracTotal.totalFree : pracTotal.totalBusy}</span>
                                        <span className="text-muted-foreground ml-1">
                                            ({viewMode === 'free' ? pracTotal.freePercentage.toFixed(1) : pracTotal.busyPercentage.toFixed(1)}%)
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}


type CalendarGapsVisualProps = {
    startDate: LocalDateTime;
    endDate: LocalDateTime;
    storeIds: number[];
    practitionerIds: number[];
    treatmentVariantIds: string[];
    timesOfDay: ('morning' | 'afternoon' | 'evening')[];
};

const CalendarGapsVisual = ({ startDate, endDate, storeIds, practitionerIds, treatmentVariantIds, timesOfDay }: CalendarGapsVisualProps) => {
    const [enabled, setEnabled] = useState<boolean>(false);

    const [viewMode, setViewMode] = useState<'percentage' | 'duration' | 'count'>('percentage');

    const calendarGapsQuery = trpc.analytics.getCalendarGaps.useQuery({
        startDate: startDate.toJson(),
        endDate: endDate.toJson(),
        storeIds: storeIds,
        practitionerIds: practitionerIds,
        treatmentVariantIds: treatmentVariantIds,
        timesOfDay: timesOfDay,
    }, {
        staleTime: 60 * 1000, // 1 minute
        enabled: enabled,
    });

    // Calculate total gap counts, minutes, and percentages across all days
    const totalGapStats = useMemo(() => {
        if (!calendarGapsQuery.data?.calendarGaps) {
            return {
                totalGapCount: 0,
                totalGapMinutes: 0,
                totalGapHours: 0,
                totalWorkingTimeMinutes: 0,
                totalWorkingTimeHours: 0,
                overallGapPercentage: 0
            };
        }

        let totalGapCount = 0;
        let totalGapMinutes = 0;
        let totalWorkingTimeMinutes = 0;

        calendarGapsQuery.data.calendarGaps.forEach(dailyData => {
            dailyData.practitioners.forEach(prac => {
                totalGapCount += prac.totalGapCount;
                totalGapMinutes += prac.totalGapMinutes;
                totalWorkingTimeMinutes += prac.totalWorkingTimeMinutes;
            });
        });

        return {
            totalGapCount,
            totalGapMinutes,
            totalGapHours: Math.round(totalGapMinutes / 60 * 100) / 100,
            totalWorkingTimeMinutes,
            totalWorkingTimeHours: Math.round(totalWorkingTimeMinutes / 60 * 100) / 100,
            overallGapPercentage: totalWorkingTimeMinutes > 0
                ? Math.round((totalGapMinutes / totalWorkingTimeMinutes) * 10000) / 100
                : 0
        };
    }, [calendarGapsQuery.data]);

    // Get unique practitioners with colors
    const practitionersArray = useMemo(() => {
        if (!calendarGapsQuery.data?.calendarGaps) {
            return [];
        }

        const practitionersSet = new Set<string>();
        calendarGapsQuery.data.calendarGaps.forEach(dailyData => {
            dailyData.practitioners.forEach(practitioner => {
                practitionersSet.add(practitioner.name);
            });
        });

        return Array.from(practitionersSet).sort().map((name, idx) => ({
            name,
            color: practitionerColors[idx % practitionerColors.length],
        }));
    }, [calendarGapsQuery.data]);

    // Transform data for gap percentage chart
    const gapPercentageOverTime = useMemo(() => {
        if (!calendarGapsQuery.data?.calendarGaps || practitionersArray.length === 0) {
            return [];
        }

        const dailyDataMap = new Map<string, Record<string, any>>();
        const practitionerNames = practitionersArray.map(p => p.name);

        calendarGapsQuery.data.calendarGaps.forEach(dataPoint => {
            const dayStr = dataPoint.date;
            let dayEntry = dailyDataMap.get(dayStr);

            if (!dayEntry) {
                dayEntry = { day: dayStr };
                // Initialize with zero for all practitioners
                for (const name of practitionerNames) {
                    dayEntry[name] = 0;
                }
                dailyDataMap.set(dayStr, dayEntry);
            }

            // Set gap percentages for each practitioner
            dataPoint.practitioners.forEach(practitioner => {
                if (practitionerNames.includes(practitioner.name)) {
                    dayEntry[practitioner.name] = practitioner.gapPercentage || 0;
                }
            });
        });

        const result = Array.from(dailyDataMap.values());
        return result.sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime());
    }, [calendarGapsQuery.data, practitionersArray]);

    // Transform data for gap count chart
    const gapCountOverTime = useMemo(() => {
        if (!calendarGapsQuery.data?.calendarGaps || practitionersArray.length === 0) {
            return [];
        }

        const dailyDataMap = new Map<string, Record<string, any>>();
        const practitionerNames = practitionersArray.map(p => p.name);

        calendarGapsQuery.data.calendarGaps.forEach(dataPoint => {
            const dayStr = dataPoint.date;
            let dayEntry = dailyDataMap.get(dayStr);

            if (!dayEntry) {
                dayEntry = { day: dayStr };
                // Initialize with zero for all practitioners
                for (const name of practitionerNames) {
                    dayEntry[name] = 0;
                }
                dailyDataMap.set(dayStr, dayEntry);
            }

            // Set gap counts for each practitioner
            dataPoint.practitioners.forEach(practitioner => {
                if (practitionerNames.includes(practitioner.name)) {
                    dayEntry[practitioner.name] = practitioner.totalGapCount;
                }
            });
        });

        const result = Array.from(dailyDataMap.values());
        return result.sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime());
    }, [calendarGapsQuery.data, practitionersArray]);

    // Transform data for gap duration chart (in minutes)
    const gapDurationOverTime = useMemo(() => {
        if (!calendarGapsQuery.data?.calendarGaps || practitionersArray.length === 0) {
            return [];
        }

        const dailyDataMap = new Map<string, Record<string, any>>();
        const practitionerNames = practitionersArray.map(p => p.name);

        calendarGapsQuery.data.calendarGaps.forEach(dataPoint => {
            const dayStr = dataPoint.date;
            let dayEntry = dailyDataMap.get(dayStr);

            if (!dayEntry) {
                dayEntry = { day: dayStr };
                // Initialize with zero for all practitioners
                for (const name of practitionerNames) {
                    dayEntry[name] = 0;
                }
                dailyDataMap.set(dayStr, dayEntry);
            }

            // Set gap durations for each practitioner
            dataPoint.practitioners.forEach(practitioner => {
                if (practitionerNames.includes(practitioner.name)) {
                    dayEntry[practitioner.name] = practitioner.totalGapMinutes;
                }
            });
        });

        const result = Array.from(dailyDataMap.values());
        return result.sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime());
    }, [calendarGapsQuery.data, practitionersArray]);

    // Calculate totals per practitioner
    const practitionerTotals = useMemo(() => {
        if (!calendarGapsQuery.data?.calendarGaps || practitionersArray.length === 0) {
            return [];
        }

        const totalsCountMap = new Map<string, number>();
        const totalsDurationMap = new Map<string, number>();
        const totalsWorkingTimeMap = new Map<string, number>();

        practitionersArray.forEach(p => {
            totalsCountMap.set(p.name, 0);
            totalsDurationMap.set(p.name, 0);
            totalsWorkingTimeMap.set(p.name, 0);
        });

        calendarGapsQuery.data.calendarGaps.forEach(dailyData => {
            dailyData.practitioners.forEach(prac => {
                if (totalsCountMap.has(prac.name)) {
                    totalsCountMap.set(prac.name, (totalsCountMap.get(prac.name) || 0) + prac.totalGapCount);
                    totalsDurationMap.set(prac.name, (totalsDurationMap.get(prac.name) || 0) + prac.totalGapMinutes);
                    totalsWorkingTimeMap.set(prac.name, (totalsWorkingTimeMap.get(prac.name) || 0) + prac.totalWorkingTimeMinutes);
                }
            });
        });

        return practitionersArray
            .map(p => ({
                name: p.name,
                totalGapCount: totalsCountMap.get(p.name) || 0,
                totalGapMinutes: totalsDurationMap.get(p.name) || 0,
                totalGapHours: Math.round((totalsDurationMap.get(p.name) || 0) / 60 * 100) / 100,
                totalWorkingTimeHours: Math.round((totalsWorkingTimeMap.get(p.name) || 0) / 60 * 100) / 100,
                gapPercentage: totalsWorkingTimeMap.get(p.name)
                    ? Math.round((totalsDurationMap.get(p.name) || 0) / (totalsWorkingTimeMap.get(p.name) || 1) * 10000) / 100
                    : 0,
                avgGapMinutes: totalsCountMap.get(p.name) ?
                    Math.round((totalsDurationMap.get(p.name) || 0) / (totalsCountMap.get(p.name) || 1) * 10) / 10 : 0,
                color: p.color,
            }))
            .sort((a, b) => {
                if (viewMode === 'percentage') return b.gapPercentage - a.gapPercentage;
                if (viewMode === 'count') return b.totalGapCount - a.totalGapCount;
                return b.totalGapMinutes - a.totalGapMinutes;
            });
    }, [calendarGapsQuery.data, practitionersArray, viewMode]);

    // Custom tooltip
    const CustomTooltipContent = ({ active, payload, label }: any) => {
        if (!active || !payload || !payload.length) {
            return null;
        }

        const dateLabel = format(new Date(label), 'EEEE, dd.MM.yyyy');
        const sortedPayload = [...payload].sort((a, b) => b.value - a.value);

        return (
            <div className="rounded-lg border bg-background p-2 shadow-md">
                <p className="text-xs font-medium">{dateLabel}</p>
                <div className="mt-1.5 space-y-1">
                    {sortedPayload.map((entry, index) => {
                        return entry.value > 0 ? (
                            <div key={`tooltip-${index}`} className="flex items-center justify-between gap-2 text-xs">
                                <div className="flex items-center gap-1.5">
                                    <span
                                        className="h-2.5 w-2.5 rounded-full"
                                        style={{ backgroundColor: entry.color }}
                                    />
                                    <span>{entry.name}</span>
                                </div>
                                <div className="font-medium tabular-nums">
                                    {viewMode === 'percentage'
                                        ? `${entry.value.toFixed(1)}%`
                                        : viewMode === 'count'
                                            ? `${entry.value} gaps`
                                            : `${entry.value} min`
                                    }
                                </div>
                            </div>
                        ) : null;
                    })}
                </div>
            </div>
        );
    };

    // Custom legend
    const CustomLegendContent = ({ payload }: any) => {
        if (!payload) return null;

        return (
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 pt-2">
                {payload?.map((entry: any, index: number) => {
                    const practitionerData = practitionerTotals.find(p => p.name === entry.value);
                    const displayValue = viewMode === 'percentage'
                        ? `${practitionerData?.gapPercentage || 0}%`
                        : viewMode === 'count'
                            ? `${practitionerData?.totalGapCount || 0} gaps`
                            : `${practitionerData?.totalGapHours || 0}h`;

                    return (
                        <div key={`legend-item-${index}`} className="flex items-center space-x-1.5">
                            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
                            <span className="text-xs">
                                {entry.value} <span className="text-xs text-muted-foreground">({displayValue})</span>
                            </span>
                        </div>
                    );
                })}
            </div>
        );
    };

    const chartConfig = useMemo(() => {
        const config: ChartConfig = {};
        practitionersArray.forEach(practitioner => {
            config[practitioner.name] = {
                label: practitioner.name,
                color: practitioner.color,
            };
        });
        return config;
    }, [practitionersArray]);

    if (!enabled) {
        return (
            <Card className='max-h-[500px]'>
                <CardHeader>
                    <CardTitle>
                        Calendar gaps
                    </CardTitle>
                    <CardDescription>
                        Enable analytics to view calendar gaps data
                    </CardDescription>
                    <div className="flex items-center space-x-2 mt-4">
                        <Switch
                            checked={enabled}
                            onCheckedChange={setEnabled}
                            id="analytics-enabled"
                        />
                        <label htmlFor="analytics-enabled" className="text-sm font-medium">
                            Enable
                        </label>
                    </div>
                </CardHeader>
            </Card>
        );
    }

    if (calendarGapsQuery.isLoading) {
        return (
            <Card className='max-h-[500px] w-full'>
                <CardHeader>
                    <CardTitle><Skeleton className="h-7 w-1/2 rounded-md" /></CardTitle>
                    <CardDescription><Skeleton className="h-4 w-3/4 rounded-md" /></CardDescription>
                </CardHeader>
                <CardContent className='h-full'>
                    <Skeleton className='w-full h-full max-h-[300px] min-h-[200px] rounded-md' />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className='w-full'>
            <CardHeader>
                <div className="flex justify-between items-center">
                    <div>
                        <CardTitle>
                            <span className='text-2xl font-normal'>
                                Total {'<'}20-min gap time: {totalGapStats.totalGapHours}h ({totalGapStats.overallGapPercentage.toFixed(1)}% of working time)
                            </span>
                        </CardTitle>
                        <CardDescription>
                            Small unbookable gaps (&lt;20 min) from {format(new Date(startDate.getDisplayDate()), "dd.MM.yyyy")} to {format(new Date(endDate.getDisplayDate()), "dd.MM.yyyy")}
                        </CardDescription>
                    </div>
                    <div className="flex items-center space-x-1">
                        <Button
                            variant={viewMode === 'percentage' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setViewMode('percentage')}
                        >
                            Percentage
                        </Button>
                        <Button
                            variant={viewMode === 'duration' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setViewMode('duration')}
                        >
                            Duration
                        </Button>
                        <Button
                            variant={viewMode === 'count' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setViewMode('count')}
                        >
                            Count
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className='h-full w-full flex flex-col'>
                <div className='h-[300px] w-full'>
                    <ChartContainer config={chartConfig} className='w-full h-full'>
                        <BarChart
                            data={viewMode === 'percentage' ? gapPercentageOverTime : viewMode === 'count' ? gapCountOverTime : gapDurationOverTime}
                            margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                            accessibilityLayer
                        >
                            <CartesianGrid vertical={false} strokeDasharray="3 3" />
                            <XAxis
                                dataKey="day"
                                tickLine={false}
                                axisLine={false}
                                tickMargin={8}
                                tickFormatter={(value) => format(new Date(value), 'dd.MM')}
                            />
                            <YAxis
                                tickLine={false}
                                axisLine={false}
                                tickMargin={3}
                                tickCount={5}
                                allowDecimals={viewMode === 'percentage'}
                                label={{
                                    value: viewMode === 'percentage' ? 'Gap %' : viewMode === 'count' ? 'Gap Count' : 'Minutes',
                                    angle: -90,
                                    position: 'insideLeft'
                                }}
                                tickFormatter={viewMode === 'percentage' ? (value) => `${value}%` : undefined}
                            />
                            <ChartTooltip
                                cursor={true}
                                content={<CustomTooltipContent />}
                            />
                            <Legend
                                content={<CustomLegendContent />}
                            />
                            {practitionersArray.map((practitioner) => (
                                <Bar
                                    key={practitioner.name}
                                    dataKey={practitioner.name}
                                    name={practitioner.name}
                                    fill={practitioner.color}
                                    stackId="a"
                                />
                            ))}
                        </BarChart>
                    </ChartContainer>
                </div>
                {practitionerTotals.length > 0 && (
                    <div className="mt-6 pt-4 border-t">
                        <h3 className="text-sm font-medium mb-3 text-foreground">
                            Aggregated Calendar Gaps:
                        </h3>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                            {practitionerTotals.map((pracTotal) => (
                                <div key={pracTotal.name} className="flex items-center space-x-2">
                                    <span
                                        className="h-3 w-3 shrink-0 rounded-full"
                                        style={{ backgroundColor: pracTotal.color }}
                                        title={pracTotal.name}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium text-foreground truncate" title={pracTotal.name}>
                                            {pracTotal.name}
                                        </p>
                                    </div>
                                    <div className="text-xs">
                                        <span className="font-semibold">
                                            {viewMode === 'percentage'
                                                ? `${pracTotal.gapPercentage.toFixed(1)}%`
                                                : viewMode === 'count'
                                                    ? `${pracTotal.totalGapCount} gaps`
                                                    : `${Math.floor(pracTotal.totalGapHours)}h${Math.round((pracTotal.totalGapHours - Math.floor(pracTotal.totalGapHours)) * 60)}m`
                                            }
                                        </span>
                                        <span className="text-muted-foreground ml-1">
                                            {viewMode === 'percentage'
                                                ? `(${Math.floor(pracTotal.totalGapHours)}h${Math.round((pracTotal.totalGapHours - Math.floor(pracTotal.totalGapHours)) * 60)}m/${Math.floor(pracTotal.totalWorkingTimeHours)}h${Math.round((pracTotal.totalWorkingTimeHours - Math.floor(pracTotal.totalWorkingTimeHours)) * 60)}m)`
                                                : `(avg ${pracTotal.avgGapMinutes}min)`
                                            }
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}


const ServiceAverageTimesVersusAllottedTimesVisual = () => {
    const [enabled, setEnabled] = useState<boolean>(false);

    const treatmentVariantDurationAnalyticsQuery = trpc.analytics.getTreatmentVariantDurationAnalytics.useQuery(undefined, {
        staleTime: 30 * 60 * 1000, // 30 minutes
        refetchInterval: 30 * 60 * 1000, // 30 minutes
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        enabled: enabled,
    });

    const treatmentVariantDurationAnalytics = treatmentVariantDurationAnalyticsQuery.data;

    // Calculate totals for header display
    const totals = treatmentVariantDurationAnalytics ? {
        totalVariants: treatmentVariantDurationAnalytics.length,
        totalAppointments: treatmentVariantDurationAnalytics.reduce((acc, variant) =>
            acc + variant.appointmentCount, 0
        )
    } : null;

    if (!enabled) {
        return (
            <Card className='max-h-[500px]'>
                <CardHeader>
                    <CardTitle>
                        Service average times versus allotted times
                    </CardTitle>
                    <CardDescription>
                        Enable analytics to view service time data
                    </CardDescription>
                    <div className="flex items-center space-x-2 mt-4">
                        <Switch
                            checked={enabled}
                            onCheckedChange={setEnabled}
                            id="analytics-enabled"
                        />
                        <label htmlFor="analytics-enabled" className="text-sm font-medium">
                            Enable
                        </label>
                    </div>
                </CardHeader>
            </Card>
        );
    }

    if (treatmentVariantDurationAnalyticsQuery.isLoading || treatmentVariantDurationAnalyticsQuery.isFetching) {
        return (
            <Card className='max-h-[500px] w-full'>
                <CardHeader>
                    <CardTitle><Skeleton className="h-7 w-1/2 rounded-md" /></CardTitle>
                    <CardDescription><Skeleton className="h-4 w-3/4 rounded-md" /></CardDescription>
                </CardHeader>
                <CardContent className='h-full'>
                    <div className="space-y-4">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="border rounded-lg p-4">
                                <div className="flex justify-between items-center mb-3">
                                    <Skeleton className="h-5 w-1/3 rounded-md" />
                                    <Skeleton className="h-6 w-20 rounded-full" />
                                </div>
                                <div className="grid grid-cols-3 gap-4 mb-3">
                                    <Skeleton className="h-4 w-full rounded-md" />
                                    <Skeleton className="h-4 w-full rounded-md" />
                                    <Skeleton className="h-4 w-full rounded-md" />
                                </div>
                                <Skeleton className="h-4 w-full rounded-full" />
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (!treatmentVariantDurationAnalytics || treatmentVariantDurationAnalytics.length === 0 || !totals) {
        return (
            <Card className='max-h-[500px] w-full'>
                <CardHeader>
                    <CardTitle>
                        <span className='text-2xl font-normal'>Treatment Times Analysis</span>
                    </CardTitle>
                    <CardDescription>
                        Average vs allotted appointment durations
                    </CardDescription>
                </CardHeader>
                <CardContent className='h-full w-full flex flex-col items-center justify-center'>
                    <div className="py-12 text-center">
                        <div className="w-16 h-16 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-medium text-foreground mb-2">No Data Available</h3>
                        <p className="text-muted-foreground">No treatment variant analytics found.</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className='max-h-[500px] w-full'>
            <CardHeader>
                <div className="flex justify-between items-center">
                    <div>
                        <CardTitle>
                            <span className='text-2xl font-normal'>
                                Total treatments: {totals.totalVariants}
                            </span>
                        </CardTitle>
                        <CardDescription>
                            Average vs allotted appointment durations across {totals.totalAppointments} appointments
                        </CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className='h-full w-full flex flex-col'>
                <div className='max-h-[300px] overflow-y-auto space-y-3'>
                    {treatmentVariantDurationAnalytics.map((variant) => {
                        const timeDifference = variant.averageDurationMinutes - variant.allottedTimeMinutes;
                        const isOverTime = timeDifference > 0;
                        const isOnTime = Math.abs(timeDifference) <= 2; // Within 2 minutes
                        const efficiency = (variant.allottedTimeMinutes / variant.averageDurationMinutes) * 100;

                        const getStatusConfig = () => {
                            if (isOnTime) return {
                                badgeVariant: 'default',
                                badgeClass: 'bg-green-100 text-green-800 border-green-200',
                                text: 'avg On Time (within 2 mins)',
                                progressColor: 'bg-green-500'
                            };
                            if (isOverTime) return {
                                badgeVariant: 'destructive',
                                badgeClass: 'bg-red-100 text-red-800 border-red-200',
                                text: `avg +${Math.abs(timeDifference).toFixed(1)} min over`,
                                progressColor: 'bg-red-500'
                            };
                            return {
                                badgeVariant: 'secondary',
                                badgeClass: 'bg-blue-100 text-blue-800 border-blue-200',
                                text: `avg ${Math.abs(timeDifference).toFixed(1)} min under`,
                                progressColor: 'bg-blue-500'
                            };
                        };

                        const statusConfig = getStatusConfig();
                        const maxTime = Math.max(variant.allottedTimeMinutes, variant.averageDurationMinutes);
                        const allottedPercentage = (variant.allottedTimeMinutes / maxTime) * 100;
                        const averagePercentage = (variant.averageDurationMinutes / maxTime) * 100;

                        return (
                            <div key={variant.id} className="border rounded-lg p-4 bg-card hover:bg-muted/30 transition-colors">
                                <div className="flex justify-between items-center mb-3">
                                    <h3 className="font-medium text-foreground">{variant.name}</h3>
                                    <div className={`px-2 py-1 rounded-full text-xs font-medium border ${statusConfig.badgeClass}`}>
                                        {statusConfig.text}
                                    </div>
                                </div>

                                <div className="grid grid-cols-4 gap-4 text-sm mb-4">
                                    <div className="flex flex-col">
                                        <span className="text-muted-foreground text-xs">Allotted</span>
                                        <span className="font-semibold text-foreground">{variant.allottedTimeMinutes}min</span>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-muted-foreground text-xs">Average</span>
                                        <span className="font-semibold text-foreground">{variant.averageDurationMinutes.toFixed(1)}min</span>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-muted-foreground text-xs">Appointments</span>
                                        <span className="font-semibold text-foreground">{variant.appointmentCount}</span>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-muted-foreground text-xs">Efficiency</span>
                                        <span className="font-semibold text-foreground">{efficiency.toFixed(1)}%</span>
                                    </div>
                                </div>

                                {/* Dual Progress Bars */}
                                <div className="space-y-2">
                                    <div className="flex justify-between text-xs text-muted-foreground">
                                        <span>Current Service Allotted Time</span>
                                        <span>{variant.allottedTimeMinutes}min</span>
                                    </div>
                                    <div className="w-full bg-muted rounded-full h-2">
                                        <div
                                            className="bg-primary h-2 rounded-full transition-all duration-300"
                                            style={{ width: `${allottedPercentage}%` }}
                                        />
                                    </div>

                                    <div className="flex justify-between text-xs text-muted-foreground mt-3">
                                        <span>Actual Service Average Time Taken</span>
                                        <span>{variant.averageDurationMinutes.toFixed(1)}min</span>
                                    </div>
                                    <div className="w-full bg-muted rounded-full h-2">
                                        <div
                                            className={`h-2 rounded-full transition-all duration-300 ${statusConfig.progressColor}`}
                                            style={{ width: `${averagePercentage}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Summary footer */}
                <div className="mt-6 pt-4 border-t">
                    <div className="flex flex-row gap-4 text-sm">
                        <div className="flex flex-col">
                            <span className="text-muted-foreground text-xs">Total Treatment Variants</span>
                            <span className="font-semibold text-foreground">{totals.totalVariants}</span>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-muted-foreground text-xs">Total Appointments</span>
                            <span className="font-semibold text-foreground">{totals.totalAppointments}</span>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};


type NewCustomersVisualProps = {
    startDate: LocalDateTime;
    endDate: LocalDateTime;
    storeIds: number[];
    practitionerIds: number[];
    treatmentVariantIds: string[];
    timesOfDay: ('morning' | 'afternoon' | 'evening')[];
}

const NewCustomersVisual = ({ startDate, endDate, storeIds, practitionerIds, treatmentVariantIds, timesOfDay }: NewCustomersVisualProps) => {
    const [enabled, setEnabled] = useState<boolean>(false);

    const chartConfig = {
        new: {
            label: "New Customers",
            color: "var(--color-green-200)",
        },
        returning: {
            label: "Returning Customers",
            color: "var(--color-primary-200)",
        },
    } satisfies ChartConfig; // Added satisfies ChartConfig for consistency, if applicable

    const newAndReturningCustomersQuery = trpc.analytics.getNewAndReturningCustomers.useQuery({
        startDate: startDate.toJson(),
        endDate: endDate.toJson(),
        storeIds: storeIds,
        practitionerIds: practitionerIds,
        treatmentVariantIds: treatmentVariantIds,
        timesOfDay: timesOfDay,
    }, {
        staleTime: 60 * 1000,
        enabled: enabled,
    });

    const newCustomers = useMemo(() => {
        return newAndReturningCustomersQuery.data?.newCustomers ?? 0;
    }, [newAndReturningCustomersQuery.data]);
    const returningCustomers = useMemo(() => {
        return newAndReturningCustomersQuery.data?.returningCustomers ?? 0;
    }, [newAndReturningCustomersQuery.data]);
    const totalCustomers = useMemo(() => {
        return newAndReturningCustomersQuery.data?.totalCustomers ?? 0;
    }, [newAndReturningCustomersQuery.data]);

    const chartData = useMemo(() => {
        return [
            { type: 'new', amount: newCustomers, fill: "var(--color-green-200)" },
            { type: 'returning', amount: returningCustomers, fill: "var(--color-primary-200)" },
        ]
    }, [newCustomers, returningCustomers]);

    if (!enabled) {
        return (
            <Card className='max-h-[500px]'>
                <CardHeader>
                    <CardTitle>
                        New customers and returning customers
                    </CardTitle>
                    <CardDescription>
                        Enable analytics to view new/returning customers data
                    </CardDescription>
                    <div className="flex items-center space-x-2 mt-4">
                        <Switch
                            checked={enabled}
                            onCheckedChange={setEnabled}
                            id="analytics-enabled"
                        />
                        <label htmlFor="analytics-enabled" className="text-sm font-medium">
                            Enable
                        </label>
                    </div>
                </CardHeader>
            </Card>
        );
    }

    if (newAndReturningCustomersQuery.isLoading) {
        return (
            <Card className='max-h-[500px]'>
                <CardHeader>
                    <CardTitle>
                        <Skeleton className="h-7 w-1/2 rounded-md" /> {/* For "Number of Customers: ..." */}
                    </CardTitle>
                    <CardDescription>
                        <Skeleton className="h-4 w-3/4 rounded-md" /> {/* For "Customers from ..." */}
                    </CardDescription>
                </CardHeader>
                <CardContent className='h-full'>
                    <Skeleton className="mx-auto aspect-square max-h-[300px] rounded-full" /> {/* For PieChart, rounded-full for pie shape */}
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className='max-h-[500px]'>
            <CardHeader>
                <CardTitle>
                    <span className='text-2xl font-normal'>Number of Customers: {totalCustomers === 0 ? '-' : totalCustomers}</span>
                </CardTitle>
                <CardDescription>
                    Customers from {format(new Date(startDate.getDisplayDate()), "dd.MM.yyyy")} to {format(new Date(endDate.getDisplayDate()), "dd.MM.yyyy")}
                </CardDescription>
            </CardHeader>
            <CardContent className='w-full h-full'>
                <ChartContainer
                    config={chartConfig}
                    className="w-full mx-auto aspect-square max-h-[300px]"
                >
                    <PieChart className='w-full'>
                        <ChartTooltip
                            content={<ChartTooltipContent nameKey="type" />}
                        />
                        <Pie
                            className='w-full'
                            data={chartData}
                            dataKey="amount"
                            label={({ payload, ...props }) => {
                                // Ensure totalCustomers is not zero to prevent division by zero NaN in label
                                const percentage = totalCustomers > 0 ? (100 * (payload.amount / totalCustomers)).toFixed(1) : 0;
                                return (
                                    <text
                                        cx={props.cx}
                                        cy={props.cy}
                                        x={props.x}
                                        y={props.y}
                                        textAnchor={props.textAnchor}
                                        dominantBaseline={props.dominantBaseline}
                                        fill="hsla(var(--foreground))"
                                    >
                                        {`${payload.type[0].toUpperCase()}${payload.type.slice(1)}`} {payload.amount} ({percentage}%)
                                    </text>
                                )
                            }}
                        />
                        <ChartLegend
                            content={<ChartLegendContent nameKey="type" />}
                            className="flex-wrap gap-2 [&>*]:basis-1/4 [&>*]:justify-center"
                        />
                    </PieChart>
                </ChartContainer>
            </CardContent>
        </Card>
    )
}



type AppointmentsGroupedByStatusVisualProps = {
    startDate: LocalDateTime;
    endDate: LocalDateTime;
    storeIds: number[];
    practitionerIds: number[];
    treatmentVariantIds: string[];
    timesOfDay: ('morning' | 'afternoon' | 'evening')[];
}

const AppointmentsGroupedByStatusVisual = ({ startDate, endDate, storeIds, practitionerIds, treatmentVariantIds, timesOfDay }: AppointmentsGroupedByStatusVisualProps) => {
    const [enabled, setEnabled] = useState<boolean>(false);

    const chartConfig = {
        [AppointmentStatus.COMPLETED]: {
            label: "Completed",
            color: "var(--color-green-200)",
        },
        [AppointmentStatus.SCHEDULED]: {
            label: "Scheduled",
            color: "var(--color-yellow-200)",
        },
        [AppointmentStatus.REQUESTED]: {
            label: "Requested",
            color: "var(--color-primary-200)",
        },
        [AppointmentStatus.DECLINED]: {
            label: "Declined",
            color: "var(--color-gray-200)",
        },
        [AppointmentStatus.CANCELLED]: {
            label: "Canceled",
            color: "var(--color-purple-200)",
        },
        [AppointmentStatus.NO_SHOW]: {
            label: "No-show",
            color: "var(--color-red-200)",
        },
        [AppointmentStatus.CALLED_OFF]: {
            label: "Called off",
            color: "var(--color-red-200)",
        },
    } satisfies ChartConfig; // Added satisfies ChartConfig for consistency, if applicable


    const appointmentsByStatusQuery = trpc.analytics.getAppointmentsByStatus.useQuery({
        startDate: startDate.toJson(),
        endDate: endDate.toJson(),
        storeIds: storeIds,
        practitionerIds: practitionerIds,
        treatmentVariantIds: treatmentVariantIds,
        timesOfDay: timesOfDay,
    }, {
        staleTime: 60 * 1000,
        enabled: enabled,
    });

    const totalAppointments = useMemo(() => {
        if (!!appointmentsByStatusQuery.data?.totalAppointments) {
            return appointmentsByStatusQuery.data.totalAppointments;
        }
        return 0;
    }, [appointmentsByStatusQuery.data]);

    const chartData = useMemo(() => {
        // Ensure AppointmentStatus and chartConfig are defined before mapping
        if (!appointmentsByStatusQuery.data?.appointmentsByStatus || !Object.values(AppointmentStatus).length) {
            return [];
        }
        return Object.values(AppointmentStatus).map((status) => {
            const amount = appointmentsByStatusQuery.data.appointmentsByStatus?.[status] ?? 0;
            return {
                status: status,
                amount: amount,
                // Ensure chartConfig[status] exists to prevent errors
                fill: chartConfig[status]?.color || "var(--color-gray-200)", // Fallback color
            }
        });
    }, [appointmentsByStatusQuery.data, chartConfig]); // Added chartConfig as dependency

    if (!enabled) {
        return (
            <Card className='max-h-[500px]'>
                <CardHeader>
                    <CardTitle>
                        Appointments status pie chart
                    </CardTitle>
                    <CardDescription>
                        Enable analytics to view appointments status data
                    </CardDescription>
                    <div className="flex items-center space-x-2 mt-4">
                        <Switch
                            checked={enabled}
                            onCheckedChange={setEnabled}
                            id="analytics-enabled"
                        />
                        <label htmlFor="analytics-enabled" className="text-sm font-medium">
                            Enable
                        </label>
                    </div>
                </CardHeader>
            </Card>
        );
    }

    if (appointmentsByStatusQuery.isLoading) {
        return (
            <Card className='max-h-[500px]'>
                <CardHeader>
                    <CardTitle>
                        <Skeleton className="h-7 w-1/2 rounded-md" /> {/* For "Number of Appointments: ..." */}
                    </CardTitle>
                    <CardDescription>
                        <Skeleton className="h-4 w-3/4 rounded-md" /> {/* For "Appointments from ..." */}
                    </CardDescription>
                </CardHeader>
                <CardContent className='h-full'>
                    <Skeleton className="mx-auto aspect-square max-h-[300px] rounded-full" /> {/* For PieChart, rounded-full for pie shape */}
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className='max-h-[500px]'>
            <CardHeader>
                <CardTitle>
                    <span className='text-2xl font-normal'>Number of Appointments: {totalAppointments === 0 ? '-' : totalAppointments}</span>
                </CardTitle>
                <CardDescription>
                    Appointments from {format(new Date(startDate.getDisplayDate()), "dd.MM.yyyy")} to {format(new Date(endDate.getDisplayDate()), "dd.MM.yyyy")}
                </CardDescription>
            </CardHeader>
            <CardContent className='w-full h-full'>
                <ChartContainer
                    config={chartConfig}
                    className="w-full mx-auto aspect-square max-h-[300px]"
                >
                    <PieChart className='w-full'>
                        <ChartTooltip
                            content={<ChartTooltipContent nameKey="status" />}
                        />
                        <Pie
                            className='w-full'
                            data={chartData.filter(d => d.amount > 0)} // Optionally filter out zero-amount slices
                            dataKey="amount"
                            nameKey="status" // Added nameKey for better tooltip and legend integration
                            label={({ payload, ...props }) => {
                                // Ensure totalAppointments is not zero to prevent division by zero NaN in label
                                const percentage = totalAppointments > 0 ? (100 * (payload.amount / totalAppointments)).toFixed(1) : 0;
                                return (
                                    <text
                                        cx={props.cx}
                                        cy={props.cy}
                                        x={props.x}
                                        y={props.y}
                                        textAnchor={props.textAnchor}
                                        dominantBaseline={props.dominantBaseline}
                                        fill="hsla(var(--foreground))"
                                    >
                                        {payload.amount} ({percentage}%)
                                    </text>
                                )
                            }}
                        />
                        <ChartLegend
                            content={<ChartLegendContent nameKey="status" />}
                            className="flex-wrap gap-2 [&>*]:basis-1/4 [&>*]:justify-center"
                        />
                    </PieChart>
                </ChartContainer>
            </CardContent>
        </Card>
    )
}

type AverageRevenuesVisualProps = {
    startDate: LocalDateTime;
    endDate: LocalDateTime;
    storeIds: number[];
    practitionerIds: number[];
    treatmentVariantIds: string[];
    timesOfDay: ('morning' | 'afternoon' | 'evening')[];
}

const AverageRevenuesVisual = ({ startDate, endDate, storeIds, practitionerIds, treatmentVariantIds, timesOfDay }: AverageRevenuesVisualProps) => {
    const [enabled, setEnabled] = useState<boolean>(false);

    const revenueOverTimeQuery = trpc.analytics.getRevenueOverTime.useQuery({
        startDate: startDate.toJson(),
        endDate: endDate.toJson(),
        storeIds: storeIds,
        practitionerIds: practitionerIds,
        treatmentVariantIds: treatmentVariantIds,
        timesOfDay: timesOfDay,
    }, {
        staleTime: 60 * 1000,
        enabled: enabled,
    });
    const newAndReturningCustomersQuery = trpc.analytics.getNewAndReturningCustomers.useQuery({
        startDate: startDate.toJson(),
        endDate: endDate.toJson(),
        storeIds: storeIds,
        practitionerIds: practitionerIds,
        treatmentVariantIds: treatmentVariantIds,
        timesOfDay: timesOfDay,
    }, {
        staleTime: 60 * 1000,
        enabled: enabled,
    });
    const appointmentsByStatusQuery = trpc.analytics.getAppointmentsByStatus.useQuery({
        startDate: startDate.toJson(),
        endDate: endDate.toJson(),
        storeIds: storeIds,
        practitionerIds: practitionerIds,
        treatmentVariantIds: treatmentVariantIds,
        timesOfDay: timesOfDay,
    }, {
        staleTime: 60 * 1000,
        enabled: enabled,
    });

    const averageRevenuePerCustomer = useMemo(() => {
        if (revenueOverTimeQuery.data?.totalRevenue !== undefined && newAndReturningCustomersQuery.data?.totalCustomers !== undefined) {
            return revenueOverTimeQuery.data.totalRevenue / newAndReturningCustomersQuery.data.totalCustomers
        }
        return 0;
    }, [revenueOverTimeQuery, newAndReturningCustomersQuery]);

    const averageRevenuePerAppointment = useMemo(() => {
        if (revenueOverTimeQuery.data?.totalRevenue !== undefined && appointmentsByStatusQuery.data) {
            const includedAppointmentStatus = [AppointmentStatus.COMPLETED, AppointmentStatus.SCHEDULED];
            const includedAppointmentCount = includedAppointmentStatus.reduce((current, status) => current + (appointmentsByStatusQuery.data.appointmentsByStatus[status] ?? 0), 0);
            return revenueOverTimeQuery.data.totalRevenue / includedAppointmentCount;
        }
        return 0;
    }, [revenueOverTimeQuery, appointmentsByStatusQuery]);

    if (!enabled) {
        return (
            <Card className='max-h-[500px]'>
                <CardHeader>
                    <CardTitle>
                        Average revenue per appointment & customer
                    </CardTitle>
                    <CardDescription>
                        Enable analytics to view average revenue data
                    </CardDescription>
                    <div className="flex items-center space-x-2 mt-4">
                        <Switch
                            checked={enabled}
                            onCheckedChange={setEnabled}
                            id="analytics-enabled"
                        />
                        <label htmlFor="analytics-enabled" className="text-sm font-medium">
                            Enable
                        </label>
                    </div>
                </CardHeader>
            </Card>
        );
    }

    return (
        <Card className='max-h-[500px]'>
            <CardHeader>
                <CardTitle>
                    <span className='text-2xl font-normal'>Average Revenues</span>
                </CardTitle>
                <CardDescription>
                    Average revenue from {format(new Date(startDate.getDisplayDate()), "dd.MM.yyyy")} to {format(new Date(endDate.getDisplayDate()), "dd.MM.yyyy")}
                </CardDescription>
            </CardHeader>
            <CardContent className='h-full'>
                <div className="grid grid-cols-2 gap-6 py-4">
                    <div className="flex flex-col items-center justify-center space-y-2">
                        <p className="text-sm text-muted-foreground">Per Customer</p>
                        <div className="flex items-baseline">
                            <span className="text-3xl font-bold">
                                {revenueOverTimeQuery.isLoading ? (
                                    <Skeleton className="h-9 w-24" />
                                ) : (
                                    `${averageRevenuePerCustomer.toLocaleString('de-DE', {
                                        style: 'currency',
                                        currency: 'EUR',
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    })}`
                                )}
                            </span>
                        </div>
                    </div>

                    <div className="flex flex-col items-center justify-center space-y-2">
                        <p className="text-sm text-muted-foreground">Per Appointment</p>
                        <div className="flex items-baseline">
                            <span className="text-3xl font-bold">
                                {appointmentsByStatusQuery.isLoading ? (
                                    <Skeleton className="h-9 w-24" />
                                ) : (
                                    `${averageRevenuePerAppointment.toLocaleString('de-DE', {
                                        style: 'currency',
                                        currency: 'EUR',
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    })}`
                                )}
                            </span>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}

type ExportToCSVButtonProps = {
    startDate: LocalDateTime;
    endDate: LocalDateTime;
    storeIds: number[];
    practitionerIds: number[];
    treatmentVariantIds: string[];
    timesOfDay: ('morning' | 'afternoon' | 'evening')[];
};

const ExportToCSVButton = ({
    startDate,
    endDate,
    storeIds,
    practitionerIds,
    treatmentVariantIds,
    timesOfDay,
}: ExportToCSVButtonProps) => {
    const [isExporting, setIsExporting] = useState(false);

    const queryOptions = {
        startDate: startDate?.toJson(),
        endDate: endDate?.toJson(),
        storeIds: storeIds,
        practitionerIds: practitionerIds,
        treatmentVariantIds: treatmentVariantIds,
        timesOfDay: timesOfDay,
    };

    const revenueOverTimeQuery = trpc.analytics.getRevenueOverTime.useQuery(queryOptions, {
        staleTime: 60 * 1000,
        enabled: !isExporting && !!startDate && !!endDate,
    });
    const newAndReturningCustomersQuery = trpc.analytics.getNewAndReturningCustomers.useQuery(queryOptions, {
        staleTime: 60 * 1000,
        enabled: !isExporting && !!startDate && !!endDate,
    });
    const appointmentsByStatusQuery = trpc.analytics.getAppointmentsByStatus.useQuery(queryOptions, {
        staleTime: 60 * 1000,
        enabled: !isExporting && !!startDate && !!endDate,
    });
    const freeAndBusyTimeslotsQuery = trpc.analytics.getDailyFreeAndBusyTimeslots.useQuery({...queryOptions, timeslotLengthMinutes: 20}, {
        staleTime: 60 * 1000,
        enabled: !isExporting && !!startDate && !!endDate,
    });

    const isLoading =
        revenueOverTimeQuery.isLoading ||
        newAndReturningCustomersQuery.isLoading ||
        appointmentsByStatusQuery.isLoading ||
        freeAndBusyTimeslotsQuery.isLoading;

    const isError =
        revenueOverTimeQuery.isError ||
        newAndReturningCustomersQuery.isError ||
        appointmentsByStatusQuery.isError ||
        freeAndBusyTimeslotsQuery.isError;

    const averageRevenuePerCustomer = useMemo(() => {
        if (
            revenueOverTimeQuery.data?.totalRevenue !== undefined &&
            newAndReturningCustomersQuery.data?.totalCustomers !== undefined &&
            newAndReturningCustomersQuery.data.totalCustomers > 0
        ) {
            return revenueOverTimeQuery.data.totalRevenue / newAndReturningCustomersQuery.data.totalCustomers;
        }
        return 0;
    }, [revenueOverTimeQuery.data, newAndReturningCustomersQuery.data]);

    const averageRevenuePerAppointment = useMemo(() => {
        if (revenueOverTimeQuery.data?.totalRevenue !== undefined && appointmentsByStatusQuery.data?.appointmentsByStatus) {
            const includedAppointmentStatus = [AppointmentStatus.COMPLETED, AppointmentStatus.SCHEDULED];
            const includedAppointmentCount = includedAppointmentStatus.reduce(
                (current, status) => current + (appointmentsByStatusQuery.data.appointmentsByStatus[status] ?? 0),
                0
            );
            if (includedAppointmentCount > 0) {
                return revenueOverTimeQuery.data.totalRevenue / includedAppointmentCount;
            }
        }
        return 0;
    }, [revenueOverTimeQuery.data, appointmentsByStatusQuery.data]);

    const handleExport = () => {
        if (!startDate || !endDate) {
            alert("Please select a valid date range to export.");
            return;
        }

        if (isLoading || isError) {
            alert("Data is still loading or an error occurred. Please try again later.");
            return;
        }

        const revenueData = revenueOverTimeQuery.data;
        const customerData = newAndReturningCustomersQuery.data;
        const statusData = appointmentsByStatusQuery.data;
        const timeslotsData = freeAndBusyTimeslotsQuery.data;

        if (!revenueData || !customerData || !statusData || !timeslotsData) {
            alert("Some data is missing. Cannot export.");
            return;
        }

        setIsExporting(true);

        try {
            const wb = XLSX.utils.book_new();

            // 1. Summary Sheet
            const summarySheetData = [
                ["Metric", "Value"],
                ["Total Revenue", revenueData.totalRevenue],
                ["Total Customers (in period)", customerData.totalCustomers],
                ["New Customers (in period)", customerData.newCustomers],
                ["Returning Customers (in period)", customerData.returningCustomers],
                ["Total Appointments (all statuses, in period)", statusData.totalAppointments],
                ["Average Revenue per Customer", parseInt(averageRevenuePerCustomer.toFixed(0))],
                ["Average Revenue per Appointment (Completed/Scheduled)", parseInt(averageRevenuePerAppointment.toFixed(0))],
            ];
            const summaryWS = XLSX.utils.aoa_to_sheet(summarySheetData);
            XLSX.utils.book_append_sheet(wb, summaryWS, "Summary");

            // 2. Revenue Over Time Sheet
            const revenueOverTimeSheetData: (string | number)[][] = [["Date", "Revenue"]];
            const sortedRevenueDates = Object.keys(revenueData.revenueByDate).sort(
                (a, b) => new Date(a).getTime() - new Date(b).getTime()
            );
            for (const date of sortedRevenueDates) {
                revenueOverTimeSheetData.push([date, revenueData.revenueByDate[date]]);
            }
            const revenueOTWS = XLSX.utils.aoa_to_sheet(revenueOverTimeSheetData);
            XLSX.utils.book_append_sheet(wb, revenueOTWS, "Revenue Over Time");

            // 3. Appointments by Status Sheet
            const appointmentsByStatusSheetData: (string | number)[][] = [["Status", "Count"]];
            for (const statusKey in AppointmentStatus) {
                const statusValue = AppointmentStatus[statusKey as keyof typeof AppointmentStatus];
                const count = statusData.appointmentsByStatus[statusValue] ?? 0;
                appointmentsByStatusSheetData.push([statusValue, count]);
            }
            const appointmentsBSWS = XLSX.utils.aoa_to_sheet(appointmentsByStatusSheetData);
            XLSX.utils.book_append_sheet(wb, appointmentsBSWS, "Appointments by Status");

            // 4. Customers Breakdown Sheet
            const customersSheetData: (string | number)[][] = [
                ["Customer Type", "Count"],
                ["New Customers", customerData.newCustomers],
                ["Returning Customers", customerData.returningCustomers],
                ["Total Customers", customerData.totalCustomers],
            ];
            const customersWS = XLSX.utils.aoa_to_sheet(customersSheetData);
            XLSX.utils.book_append_sheet(wb, customersWS, "Customer Breakdown");

            // 5. Practitioner Timeslots Sheet
            const practitionerTimeslotsSheetData: (string | number)[][] = [];
            const timeslotHeader = ["Date", "Practitioner Name", "Free Timeslots", "Busy Timeslots", "Free %", "Busy %"];
            practitionerTimeslotsSheetData.push(timeslotHeader);

            if (timeslotsData.dailyFreeAndBusyTimeslots && timeslotsData.dailyFreeAndBusyTimeslots.length > 0) {
                const uniquePractitionerNames = new Set<string>();
                const uniqueDatesFromData = new Set<string>();
                const rawDataByDateAndPractitioner: Record<string, Record<string, { free_timeslots: number; busy_timeslots: number }>> = {};

                timeslotsData.dailyFreeAndBusyTimeslots.forEach(dailyData => {
                    uniqueDatesFromData.add(dailyData.date);
                    if (!rawDataByDateAndPractitioner[dailyData.date]) {
                        rawDataByDateAndPractitioner[dailyData.date] = {};
                    }
                    dailyData.practitioners.forEach(prac => {
                        uniquePractitionerNames.add(prac.name);
                        rawDataByDateAndPractitioner[dailyData.date][prac.name] = {
                            free_timeslots: prac.free_timeslots,
                            busy_timeslots: prac.busy_timeslots,
                        };
                    });
                });

                const sortedUniqueDates = Array.from(uniqueDatesFromData).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
                const sortedUniquePractitionerNames = Array.from(uniquePractitionerNames).sort((a, b) => a.localeCompare(b));

                const allPractitionerSlotsRows: (string | number)[][] = [];

                sortedUniqueDates.forEach(dateStr => {
                    sortedUniquePractitionerNames.forEach(practitionerName => {
                        const pracData = rawDataByDateAndPractitioner[dateStr]?.[practitionerName];

                        let free_timeslots = 0;
                        let busy_timeslots = 0;

                        if (pracData) {
                            free_timeslots = pracData.free_timeslots;
                            busy_timeslots = pracData.busy_timeslots;
                        }

                        const totalSlots = free_timeslots + busy_timeslots;
                        const freePercentage = totalSlots > 0 ? (free_timeslots / totalSlots) * 100 : 0;
                        const busyPercentage = totalSlots > 0 ? (busy_timeslots / totalSlots) * 100 : 0;

                        allPractitionerSlotsRows.push([
                            dateStr,
                            practitionerName,
                            free_timeslots,
                            busy_timeslots,
                            parseFloat(freePercentage.toFixed(2)),
                            parseFloat(busyPercentage.toFixed(2))
                        ]);
                    });
                });
                practitionerTimeslotsSheetData.push(...allPractitionerSlotsRows);
            }
            const practitionerTimeslotsWS = XLSX.utils.aoa_to_sheet(practitionerTimeslotsSheetData);
            XLSX.utils.book_append_sheet(wb, practitionerTimeslotsWS, "Practitioner Timeslots");

            const formattedStartDate = startDate.getDisplayDate();
            const formattedEndDate = endDate.getDisplayDate();
            const filename = `analytics_export_${formattedStartDate}_to_${formattedEndDate}.xlsx`;

            XLSX.writeFile(wb, filename);

        } catch (error) {
            console.error("Error generating Excel file:", error);
            alert("An error occurred while generating the export file.");
        } finally {
            setIsExporting(false);
        }
    };

    const canExport =
        !isLoading &&
        !isError &&
        !!startDate &&
        !!endDate &&
        !!revenueOverTimeQuery.data &&
        !!newAndReturningCustomersQuery.data &&
        !!appointmentsByStatusQuery.data &&
        !!freeAndBusyTimeslotsQuery.data;

    return (
        <div>
            <Button
                variant='outline'
                onClick={handleExport}
                disabled={!canExport || isExporting}
                style={{
                    padding: '10px 15px',
                    cursor: (!canExport || isExporting) ? 'not-allowed' : 'pointer',
                    opacity: (!canExport || isExporting) ? 0.6 : 1,
                }}
            >
                <Save className="mr-2 h-4 w-4" />
                {isExporting
                    ? "Exporting..."
                    : (isLoading && !!startDate && !!endDate) // Show loading only if dates are set and queries are running
                        ? "Loading Data..."
                        : "Export to XLSX"}
            </Button>
            {isError && <p style={{ color: 'red', marginTop: '8px' }}>Error loading data. Cannot export.</p>}
        </div>
    );
};

const AnalyticsPage = () => {
    const allStoresQuery = trpc.stores.getAllSimple.useQuery(undefined, {
        staleTime: 60 * 1000,
    });
    const allPractitionersQuery = trpc.practitioners.getAll.useQuery({
        pageSize: 1000,
    }, {
        staleTime: 60 * 1000,
    });
    const allTreatmentVariantsQuery = trpc.treatmentVariants.getAll.useQuery({
        storeId: null,
        pageSize: 1000,
    }, {
        staleTime: 60 * 1000,
    });

    const allStores = useMemo(() => {
        if (allStoresQuery.data) {
            return allStoresQuery.data;
        }
        return [];
    }, [allStoresQuery]);

    const allPractitioners = useMemo(() => {
        if (allPractitionersQuery.data?.data) {
            return allPractitionersQuery.data.data;
        }
        return [];
    }, [allPractitionersQuery]);

    const allTreatmentVariants = useMemo(() => {
        if (allTreatmentVariantsQuery.data?.data) {
            return allTreatmentVariantsQuery.data.data;
        }
        return [];
    }, [allTreatmentVariantsQuery]);

    const [dateFrom, setDateFrom] = useState<LocalDateTime | undefined>(LocalDateTime.fromUTCDate(startOfMonth(new Date()), 'Europe/Berlin'));
    const [dateTo, setDateTo] = useState<LocalDateTime | undefined>(LocalDateTime.fromUTCDate(endOfMonth(new Date()), 'Europe/Berlin'));
    const [selectedStores, setSelectedStores] = useState<typeof allStores>([]);
    const [selectedPractitioners, setSelectedPractitioners] = useState<typeof allPractitioners>([]);
    const [selectedTreatmentVariants, setSelectedTreatmentVariants] = useState<typeof allTreatmentVariants>([]);
    const [timesOfDay, setTimesOfDay] = useState<('morning' | 'afternoon' | 'evening')[]>([]);

    // Handlers for multi-select functionality
    const toggleStore = (store: any) => {
        if (selectedStores.some(s => s.id === store.id)) {
            setSelectedStores(selectedStores.filter(s => s.id !== store.id));
        } else {
            setSelectedStores([...selectedStores, store]);
        }
    };

    const togglePractitioner = (practitioner: any) => {
        if (selectedPractitioners.some(p => p.id === practitioner.id)) {
            setSelectedPractitioners(selectedPractitioners.filter(p => p.id !== practitioner.id));
        } else {
            setSelectedPractitioners([...selectedPractitioners, practitioner]);
        }
    };

    const toggleTreatmentVariant = (variant: any) => {
        if (selectedTreatmentVariants.some(v => v.id === variant.id)) {
            setSelectedTreatmentVariants(selectedTreatmentVariants.filter(v => v.id !== variant.id));
        } else {
            setSelectedTreatmentVariants([...selectedTreatmentVariants, variant]);
        }
    };

    return (
        <Content>
            <div className="container mx-auto py-10">
                <h1 className="text-3xl font-bold mb-4">Samson Analytics</h1>

                {/* Date Range Selectors */}
                <div className='w-full grid gap-4'>
                    <div className='col-span-12'>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">Filters</CardTitle>
                                <CardDescription>Filter analytics by date, store, practitioner, treatment variant.</CardDescription>
                            </CardHeader>
                            <CardContent className="flex flex-col sm:flex-row gap-4">
                                {/* Date Range Picker */}
                                <div className="flex-1">
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                className="w-full justify-start text-left font-normal"
                                            >
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {dateFrom && dateTo ? (
                                                    `${format(new Date(dateFrom.getDisplayDate()), "dd.MM.yyyy")} - ${format(new Date(dateTo.getDisplayDate()), "dd.MM.yyyy")}`
                                                ) : (
                                                    "Select date range"
                                                )}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0">
                                            <Calendar
                                                mode="range"
                                                selected={{
                                                    from: dateFrom?.toTimezonedDate(),
                                                    to: dateTo?.toTimezonedDate(),
                                                }}
                                                timeZone="Europe/Berlin"
                                                onSelect={(range) => {
                                                    if(range?.from) {
                                                        setDateFrom(LocalDateTime.fromLocalDate(range.from, dateFrom?.getTimezone() || 'Europe/Berlin'));
                                                    }
                                                    if(range?.to) {
                                                        setDateTo(LocalDateTime.fromLocalDate(range.to, dateFrom?.getTimezone() || 'Europe/Berlin'));
                                                    }
                                                }}
                                            />
                                        </PopoverContent>
                                    </Popover>
                                    {/* Date Range Validation Message */}
                                    {dateFrom && dateTo &&
                                        Math.abs(dateTo.valueOf() - dateFrom.valueOf()) / (1000 * 60 * 60 * 24) > 45 && (
                                            <div className="text-sm text-red-600 mt-2">
                                                Der Datumsbereich darf maximal 45 Tage umfassen.
                                            </div>
                                        )}
                                </div>

                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className="justify-between"
                                            disabled={allStoresQuery.isLoading}
                                        >
                                            {selectedStores.length === 0
                                                ? "Select stores"
                                                : `${selectedStores.length} store${selectedStores.length > 1 ? 's' : ''} selected`
                                            }
                                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="p-0">
                                        <Command>
                                            <CommandInput placeholder="Search stores..." />
                                            <CommandEmpty>No store found.</CommandEmpty>
                                            <CommandGroup>
                                                <CommandList>
                                                    {allStores && allStores.map((store) => (
                                                        <CommandItem
                                                            key={store.id}
                                                            value={store.name}
                                                            onSelect={() => toggleStore(store)}
                                                            className="flex items-center justify-between"
                                                        >
                                                            {store.name}
                                                            {selectedStores.some(s => s.id === store.id) && (
                                                                <CheckIcon className="h-4 w-4" />
                                                            )}
                                                        </CommandItem>
                                                    ))}
                                                </CommandList>
                                            </CommandGroup>
                                        </Command>
                                    </PopoverContent>
                                </Popover>

                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className="justify-between"
                                            disabled={allPractitionersQuery.isLoading}
                                        >
                                            {selectedPractitioners.length === 0
                                                ? "Select practitioners"
                                                : `${selectedPractitioners.length} practitioner${selectedPractitioners.length > 1 ? 's' : ''} selected`
                                            }
                                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="p-0">
                                        <Command>
                                            <CommandInput placeholder="Search practitioners..." />
                                            <CommandEmpty>No practitioner found.</CommandEmpty>
                                            <CommandGroup>
                                                <CommandList>
                                                    {allPractitioners.map((practitioner) => (
                                                        <CommandItem
                                                            key={practitioner.id}
                                                            value={(`${practitioner.title && practitioner.title.length > 0 ? `${practitioner.title} ` : ''}${practitioner.firstName} ${practitioner.lastName} ${practitioner.practitioner_category?.name ?? ''} ${practitioner.id}`) || practitioner.id.toString()}
                                                            onSelect={() => togglePractitioner(practitioner)}
                                                            className="flex items-center justify-between"
                                                        >
                                                            <PractitionerNameWithCategory
                                                                firstName={practitioner.firstName}
                                                                lastName={practitioner.lastName}
                                                                title={practitioner.title}
                                                                categoryName={practitioner.practitioner_category?.name}
                                                            />
                                                            {selectedPractitioners.some(p => p.id === practitioner.id) && (
                                                                <CheckIcon className="h-4 w-4" />
                                                            )}
                                                        </CommandItem>
                                                    ))}
                                                </CommandList>
                                            </CommandGroup>
                                        </Command>
                                    </PopoverContent>
                                </Popover>

                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className="justify-between"
                                            disabled={allTreatmentVariantsQuery.isLoading}
                                        >
                                            {selectedTreatmentVariants.length === 0
                                                ? "Select treatment variants"
                                                : `${selectedTreatmentVariants.length} variant${selectedTreatmentVariants.length > 1 ? 's' : ''} selected`
                                            }
                                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="p-0">
                                        <Command>
                                            <CommandInput placeholder="Search treatment variants..." />
                                            <CommandEmpty>No treatment variant found.</CommandEmpty>
                                            <CommandGroup>
                                                <CommandList>
                                                    {allTreatmentVariants.map((variant) => (
                                                        <CommandItem
                                                            key={variant.id}
                                                            value={variant.name || variant.id.toString()}
                                                            onSelect={() => toggleTreatmentVariant(variant)}
                                                            className="flex items-center justify-between"
                                                        >
                                                            {variant.name || `Variant ${variant.id}`}
                                                            {selectedTreatmentVariants.some(v => v.id === variant.id) && (
                                                                <CheckIcon className="h-4 w-4" />
                                                            )}
                                                        </CommandItem>
                                                    ))}
                                                </CommandList>
                                            </CommandGroup>
                                        </Command>
                                    </PopoverContent>
                                </Popover>

                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className="justify-between"
                                        >
                                            {timesOfDay.length === 0
                                                ? "Select time of day"
                                                : `${timesOfDay.length} time${timesOfDay.length > 1 ? 's' : ''} selected`
                                            }
                                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="p-0">
                                        <Command>
                                            <CommandInput placeholder="Search time of day..." />
                                            <CommandEmpty>No time of day found.</CommandEmpty>
                                            <CommandGroup>
                                                <CommandList>
                                                    <CommandItem
                                                        value="morning"
                                                        onSelect={() => {
                                                            if (timesOfDay.includes("morning")) {
                                                                setTimesOfDay(timesOfDay.filter(t => t !== "morning"));
                                                            } else {
                                                                setTimesOfDay([...timesOfDay, "morning"]);
                                                            }
                                                        }}
                                                        className="flex items-center justify-between"
                                                    >
                                                        Morning (12 and earlier)
                                                        {timesOfDay.includes("morning") && (
                                                            <CheckIcon className="h-4 w-4" />
                                                        )}
                                                    </CommandItem>
                                                    <CommandItem
                                                        value="afternoon"
                                                        onSelect={() => {
                                                            if (timesOfDay.includes("afternoon")) {
                                                                setTimesOfDay(timesOfDay.filter(t => t !== "afternoon"));
                                                            } else {
                                                                setTimesOfDay([...timesOfDay, "afternoon"]);
                                                            }
                                                        }}
                                                        className="flex items-center justify-between"
                                                    >
                                                        Afternoon (12-16)
                                                        {timesOfDay.includes("afternoon") && (
                                                            <CheckIcon className="h-4 w-4" />
                                                        )}
                                                    </CommandItem>
                                                    <CommandItem
                                                        value="evening"
                                                        onSelect={() => {
                                                            if (timesOfDay.includes("evening")) {
                                                                setTimesOfDay(timesOfDay.filter(t => t !== "evening"));
                                                            } else {
                                                                setTimesOfDay([...timesOfDay, "evening"]);
                                                            }
                                                        }}
                                                        className="flex items-center justify-between"
                                                    >
                                                        Evening (later than 16)
                                                        {timesOfDay.includes("evening") && (
                                                            <CheckIcon className="h-4 w-4" />
                                                        )}
                                                    </CommandItem>
                                                </CommandList>
                                            </CommandGroup>
                                        </Command>
                                    </PopoverContent>
                                </Popover>

                                {/* {!!dateFrom && !!dateTo && (
                                    <ExportToCSVButton
                                        startDate={dateFrom}
                                        endDate={dateTo}
                                        storeIds={selectedStores.map((store) => store.id)}
                                        practitionerIds={selectedPractitioners.map((practitioner) => practitioner.id)}
                                        treatmentVariantIds={selectedTreatmentVariants.map((variant) => variant.id)}
                                    />
                                )} */}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Revenue over time */}
                    <div className='col-span-12'>
                        {!!dateFrom && !!dateTo && Math.abs(dateTo.valueOf() - dateFrom.valueOf()) / (1000 * 60 * 60 * 24) <= 45 && (
                            <RevenueGraphVisual
                                key={"revenue" + dateFrom.valueOf() + "-" + dateTo.valueOf() + "-" + selectedStores.map((store) => store.id).join(",") + "-" + selectedPractitioners.map((practitioner) => practitioner.id).join(",") + "-" + selectedTreatmentVariants.map((variant) => variant.id).join(",") + "-" + timesOfDay.join(",")}
                                startDate={dateFrom}
                                endDate={dateTo}
                                storeIds={selectedStores.map((store) => store.id)}
                                practitionerIds={selectedPractitioners.map((practitioner) => practitioner.id)}
                                treatmentVariantIds={selectedTreatmentVariants.map((variant) => variant.id)}
                                timesOfDay={timesOfDay}
                            />
                        )}
                    </div>

                    {/* Customers count */}
                    <div className='col-span-6'>
                        {!!dateFrom && !!dateTo && Math.abs(dateTo.valueOf() - dateFrom.valueOf()) / (1000 * 60 * 60 * 24) <= 45 && (
                            <NewCustomersVisual
                                key={"new-customers" + dateFrom.valueOf() + "-" + dateTo.valueOf() + "-" + selectedStores.map((store) => store.id).join(",") + "-" + selectedPractitioners.map((practitioner) => practitioner.id).join(",") + "-" + selectedTreatmentVariants.map((variant) => variant.id).join(",") + "-" + timesOfDay.join(",")}
                                startDate={dateFrom}
                                endDate={dateTo}
                                storeIds={selectedStores.map((store) => store.id)}
                                practitionerIds={selectedPractitioners.map((practitioner) => practitioner.id)}
                                treatmentVariantIds={selectedTreatmentVariants.map((variant) => variant.id)}
                                timesOfDay={timesOfDay}
                            />
                        )}
                    </div>

                    {/* Number of appointments per status */}
                    <div className='col-span-6'>
                        {!!dateFrom && !!dateTo && Math.abs(dateTo.valueOf() - dateFrom.valueOf()) / (1000 * 60 * 60 * 24) <= 45 && (
                            <AppointmentsGroupedByStatusVisual
                                key={"appointments-status" + dateFrom.valueOf() + "-" + dateTo.valueOf() + "-" + selectedStores.map((store) => store.id).join(",") + "-" + selectedPractitioners.map((practitioner) => practitioner.id).join(",") + "-" + selectedTreatmentVariants.map((variant) => variant.id).join(",") + "-" + timesOfDay.join(",")}
                                startDate={dateFrom}
                                endDate={dateTo}
                                storeIds={selectedStores.map((store) => store.id)}
                                practitionerIds={selectedPractitioners.map((practitioner) => practitioner.id)}
                                treatmentVariantIds={selectedTreatmentVariants.map((variant) => variant.id)}
                                timesOfDay={timesOfDay}
                            />
                        )}
                    </div>


                    {/* Average revenue per customer and average revenue per appointment */}
                    <div className='col-span-4'>
                        {!!dateFrom && !!dateTo && Math.abs(dateTo.valueOf() - dateFrom.valueOf()) / (1000 * 60 * 60 * 24) <= 45 && (
                            <AverageRevenuesVisual
                                key={"avg-revenues" + dateFrom.valueOf() + "-" + dateTo.valueOf() + "-" + selectedStores.map((store) => store.id).join(",") + "-" + selectedPractitioners.map((practitioner) => practitioner.id).join(",") + "-" + selectedTreatmentVariants.map((variant) => variant.id).join(",") + "-" + timesOfDay.join(",")}
                                startDate={dateFrom}
                                endDate={dateTo}
                                storeIds={selectedStores.map((store) => store.id)}
                                practitionerIds={selectedPractitioners.map((practitioner) => practitioner.id)}
                                treatmentVariantIds={selectedTreatmentVariants.map((variant) => variant.id)}
                                timesOfDay={timesOfDay}
                            />
                        )}
                    </div>


                    {/* Timeslots per day grouped by practitioner */}
                    <div className='col-span-6'>
                        {!!dateFrom && !!dateTo && Math.abs(dateTo.valueOf() - dateFrom.valueOf()) / (1000 * 60 * 60 * 24) <= 45 && (
                            <FreeAndBusyTimeslotsVisual
                                key={"free-busy-timeslots-20-" + dateFrom.valueOf() + "-" + dateTo.valueOf() + "-" + selectedStores.map((store) => store.id).join(",") + "-" + selectedPractitioners.map((practitioner) => practitioner.id).join(",") + "-" + selectedTreatmentVariants.map((variant) => variant.id).join(",") + "-" + timesOfDay.join(",")}
                                startDate={dateFrom}
                                endDate={dateTo}
                                storeIds={selectedStores.map((store) => store.id)}
                                practitionerIds={selectedPractitioners.map((practitioner) => practitioner.id)}
                                treatmentVariantIds={selectedTreatmentVariants.map((variant) => variant.id)}
                                timesOfDay={timesOfDay}
                                timeslotLengthMinutes={20}
                            />
                        )}
                    </div>
                    <div className='col-span-6'>
                        {!!dateFrom && !!dateTo && Math.abs(dateTo.valueOf() - dateFrom.valueOf()) / (1000 * 60 * 60 * 24) <= 45 && (
                            <FreeAndBusyTimeslotsVisual
                                key={"free-busy-timeslots-40-" + dateFrom.valueOf() + "-" + dateTo.valueOf() + "-" + selectedStores.map((store) => store.id).join(",") + "-" + selectedPractitioners.map((practitioner) => practitioner.id).join(",") + "-" + selectedTreatmentVariants.map((variant) => variant.id).join(",") + "-" + timesOfDay.join(",")}
                                startDate={dateFrom}
                                endDate={dateTo}
                                storeIds={selectedStores.map((store) => store.id)}
                                practitionerIds={selectedPractitioners.map((practitioner) => practitioner.id)}
                                treatmentVariantIds={selectedTreatmentVariants.map((variant) => variant.id)}
                                timesOfDay={timesOfDay}
                                timeslotLengthMinutes={40}
                            />
                        )}
                    </div>

                    <div className='col-span-6'>
                        {!!dateFrom && !!dateTo && Math.abs(dateTo.valueOf() - dateFrom.valueOf()) / (1000 * 60 * 60 * 24) <= 45 && (
                            <CalendarGapsVisual
                                key={"calendar-gaps" + dateFrom.valueOf() + "-" + dateTo.valueOf() + "-" + selectedStores.map((store) => store.id).join(",") + "-" + selectedPractitioners.map((practitioner) => practitioner.id).join(",") + "-" + selectedTreatmentVariants.map((variant) => variant.id).join(",") + "-" + timesOfDay.join(",")}
                                startDate={dateFrom}
                                endDate={dateTo}
                                storeIds={selectedStores.map((store) => store.id)}
                                practitionerIds={selectedPractitioners.map((practitioner) => practitioner.id)}
                                treatmentVariantIds={selectedTreatmentVariants.map((variant) => variant.id)}
                                timesOfDay={timesOfDay}
                            />
                        )}
                    </div>
                </div>

                {!!dateFrom && !!dateTo && Math.abs(dateTo.valueOf() - dateFrom.valueOf()) / (1000 * 60 * 60 * 24) <= 45 && (
                    <>
                        <h1 className="text-3xl font-bold mt-4">Service average times vs allotted times</h1>
                        <ServiceAverageTimesVersusAllottedTimesVisual 
                            key={"service-time-taken" + dateFrom.valueOf() + "-" + dateTo.valueOf() + "-" + selectedStores.map((store) => store.id).join(",") + "-" + selectedPractitioners.map((practitioner) => practitioner.id).join(",") + "-" + selectedTreatmentVariants.map((variant) => variant.id).join(",")}
                        />
                    </>
                )}
            </div>
        </Content>
    )
};


export default AnalyticsPage;